/**
 * Getting a thing OUT of the app.
 *
 * A file to the share sheet on a phone, a download on a laptop, a link to wherever links go.
 * One function per shape of thing and each one answers what it did -- 'shared', 'copied',
 * 'downloaded', 'cancelled' -- so the page can say so in the maker's words rather than
 * guessing whether the sheet opened.
 *
 * Native goes through Capacitor's Filesystem and Share: a WKWebView cannot hand a Blob to
 * the system share sheet, but it can write it to the cache and share the file's URI.
 */
import { Capacitor } from '@capacitor/core'

export const isNative = () => Capacitor.isNativePlatform()

/**
 * A phone gets the share sheet; a laptop gets a download.
 *
 * Chrome on a Mac has navigator.share too, and it opens the system sheet -- which is not
 * what anybody at a desk expects when they press "PDF", and in a headless browser it never
 * comes back at all. Touch plus a phone's user agent is the test for "sheet".
 */
const handheld = () => typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0
  && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)

export async function shareFile(blob, filename, opts = {}) {
  if (isNative()) {
    const { Filesystem, Directory } = await import('@capacitor/filesystem')
    const { Share } = await import('@capacitor/share')
    const data = await toBase64(blob)
    const r = await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache })
    try {
      await Share.share({ title: opts.title, text: opts.text, files: [r.uri] })
      return 'shared'
    } catch (e) {
      if (/cancel/i.test(String(e?.message ?? e))) return 'cancelled'
      throw e
    }
  }
  const file = new File([blob], filename, { type: blob.type })
  if (handheld() && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: opts.title, text: opts.text })
      return 'shared'
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled'
      // Some browsers claim they can share a file and then cannot; fall through.
    }
  }
  download(blob, filename)
  return 'downloaded'
}

/**
 * Several files at once, as one share.
 *
 * The handover a maker wants is not one file, it is the set: the design, the drawings, the
 * cabinet list, the CAD. Both share sheets take an array, so the set travels as a set --
 * one tap, one email, four attachments. A browser that cannot take files falls back to
 * downloading them one after another, which is the same outcome by a duller road.
 *
 * @param {Array<{blob: Blob, filename: string}>} list
 */
export async function shareFiles(list, opts = {}) {
  if (!list.length) return 'cancelled'
  if (list.length === 1) return shareFile(list[0].blob, list[0].filename, opts)

  if (isNative()) {
    const { Filesystem, Directory } = await import('@capacitor/filesystem')
    const { Share } = await import('@capacitor/share')
    const uris = []
    for (const f of list) {
      const data = await toBase64(f.blob)
      const r = await Filesystem.writeFile({ path: f.filename, data, directory: Directory.Cache })
      uris.push(r.uri)
    }
    try {
      await Share.share({ title: opts.title, text: opts.text, files: uris })
      return 'shared'
    } catch (e) {
      if (/cancel/i.test(String(e?.message ?? e))) return 'cancelled'
      throw e
    }
  }

  const files = list.map((f) => new File([f.blob], f.filename, { type: f.blob.type }))
  if (handheld() && navigator.canShare?.({ files })) {
    try {
      await navigator.share({ files, title: opts.title, text: opts.text })
      return 'shared'
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled'
    }
  }
  for (const f of list) download(f.blob, f.filename)
  return 'downloaded'
}

/**
 * Share a file that is ALREADY on the device, by path -- native only.
 *
 * Every other export here builds a Blob in JavaScript and writes it out through
 * Filesystem.writeFile before sharing it. A RoomPlan scan is written to disk by Swift
 * directly, sometimes tens of megabytes of mesh and texture, and reading that back into JS
 * as a Blob only to re-write it to the same cache directory would double the memory and the
 * bridge traffic for no reason: `Share.share` takes a native file URL directly on iOS.
 *
 * On iOS specifically, handing a `.usdz` to the share sheet is also how this app "shows"
 * a 3D file at all -- the share sheet renders it as a live, spinnable Quick Look preview
 * before anyone picks where it goes, which is the same trick `ar.js` relies on for viewing
 * the designed kitchen. One tap is both "let me look at this" and "send it to someone".
 */
export async function shareNativeFile(path, opts = {}) {
  if (!isNative()) throw new Error('shareNativeFile is native-only; the file never left the device')
  const { Share } = await import('@capacitor/share')
  try {
    await Share.share({ title: opts.title, text: opts.text, url: path })
    return 'shared'
  } catch (e) {
    if (/cancel/i.test(String(e?.message ?? e))) return 'cancelled'
    throw e
  }
}

export async function shareLink(url, opts = {}) {
  if (isNative()) {
    const { Share } = await import('@capacitor/share')
    try { await Share.share({ title: opts.title, text: opts.text, url }); return 'shared' } catch (e) {
      if (/cancel/i.test(String(e?.message ?? e))) return 'cancelled'
      throw e
    }
  }
  if (handheld() && navigator.share) {
    try { await navigator.share({ title: opts.title, text: opts.text, url }); return 'shared' } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled'
    }
  }
  await copyText(url)
  return 'copied'
}

export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return 'copied' } catch {
    // The old way, for a page without clipboard permission.
    const ta = document.createElement('textarea')
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'
    document.body.appendChild(ta); ta.select()
    const ok = document.execCommand?.('copy')
    ta.remove()
    return ok ? 'copied' : 'failed'
  }
}

export function download(blob, filename) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(a.href), 4000)
}

function toBase64(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(String(fr.result).replace(/^data:[^;]*;base64,/, ''))
    fr.onerror = () => reject(fr.error)
    fr.readAsDataURL(blob)
  })
}
